import {
  AbilityBuilder,
  AbilityClass,
  ExtractSubjectType,
  ForbiddenError,
  InferSubjects,
  MatchConditions,
  PureAbility
} from '@casl/ability';
import { castArray } from 'lodash';

import { IComments, IProfiles, IRoutes } from './types';

const lambdaMatcher = (matchConditions: MatchConditions) => matchConditions;
export type UserSubjects = 'Patient' | 'Staff' | 'Organization' | 'Admin' | 'Guest' | 'Public';

export type Subjects =
  InferSubjects<'Event' |
  Pick<IRoutes, 'meta'> |
  Pick<IComments, 'meta'> |
  Pick<IProfiles, 'meta'> |
  UserSubjects |
  'Public' |
  'AntibioticStewardship' |
  'Appointment' |
  'AccessGroup' |
  'Account' |
  'Careplan' |
  'ClinicalAssessment' |
  'Condition' |
  'Dashboard' |
  'Demographic' |
  'DiagnosticReport' |
  'Employee' |
  'Medication' |
  'Observation' |
  'Order' |
  'Patient' |
  'Practitioner' |
  'Product' |
  'Staff' |
  'Store' |
  'Survey' |
  'Task' |
  'Organization' |
  'all'>;
type Actions = string;
type PossibleAbilities = [Actions, Subjects];

type SomeOf = (
  ability: AppAbility,
  resource: Subjects,
  actions: string[]
) => boolean;

export type AppAbility = PureAbility<PossibleAbilities, MatchConditions>
& { someOf: SomeOf };
export const AppAbilities = PureAbility as AbilityClass<AppAbility>;

AppAbilities.prototype.someOf = (
  ability: AppAbility,
  resource: Subjects,
  actions: string[]
) => castArray(actions).some((action) => ability.can(action, resource));

/**
 * Generates rules for passed user based on their userId and the type of permissions they have.
 * Admins override any restrictions and will have full access to all CRUD operations
 * @param user
 */
export const defineRulesContext = () => {
  const { can, rules } = new AbilityBuilder(AppAbilities);
  // === If no user session present, restrict access to Auth Routes; @formatter:off
  can('Public', 'Public');
  return rules;
};

/**
 * Factory function that returns the abilities for the user provided
 *
 * `Example:` const ability = defineRulesContext()
 */
export const buildAbilityFor = ()
: AppAbility => new AppAbilities(defineRulesContext(), {
  conditionsMatcher: lambdaMatcher,
  detectSubjectType: (subject) => subject?.meta?.kind as ExtractSubjectType<Subjects>,
});

ForbiddenError.setDefaultMessage((error) => `You are not allowed to ${error.action} on ${error.subjectType}`);
